<template>
  <v-container fluid class="pa-4 inscritos-ciclo">
    <Alerta v-if="respuestaAlerta" :parametros="parametros" />

    <!-- Encabezado + filtros -->
    <v-card class="shadowCard mb-4" elevation="0">
      <v-card-text class="encabezado__contenido">
        <div class="d-flex align-center encabezado__titulo">
          <v-avatar color="primary" size="40" rounded class="mr-3">
            <v-icon dark>mdi-account-group</v-icon>
          </v-avatar>
          <div class="encabezado__textos">
            <div class="text-subtitle-1 font-weight-bold grey--text text--darken-3">Inscritos por ciclo</div>
            <div class="text-caption grey--text encabezado__subtitulo">
              <template v-if="rango">
                INBI y FAST · {{ rango.ciclos[0].ciclo }} a {{ rango.ciclos[rango.ciclos.length - 1].ciclo }}
              </template>
              <template v-else>Inscritos por plantel en cada ciclo</template>
            </div>
          </div>
        </div>

        <div class="encabezado__filtros">
          <v-autocomplete
            v-model="anio"
            :items="anios"
            label="Año"
            outlined
            dense
            hide-details
            :loading="cargandoCiclos"
            class="filtro__anio"
          />
          <v-autocomplete
            v-model="idCicloInicio"
            :items="ciclosInicio"
            item-text="ciclo"
            item-value="id_ciclo"
            label="Ciclo inicial"
            outlined
            dense
            hide-details
            class="filtro__ciclo"
          />
          <v-autocomplete
            v-model="idCicloFin"
            :items="ciclosFin"
            item-text="ciclo"
            item-value="id_ciclo"
            label="Ciclo final"
            outlined
            dense
            hide-details
            class="filtro__ciclo"
          />
          <v-autocomplete
            v-model="idPlantel"
            :items="opcionesPlantel"
            item-text="plantel"
            item-value="id_plantel"
            label="Plantel"
            outlined
            dense
            hide-details
            prepend-inner-icon="mdi-store-outline"
            class="filtro__plantel"
          />
          <v-select
            v-model="adeudo"
            :items="opcionesAdeudo"
            label="Adeudo"
            outlined
            dense
            hide-details
            prepend-inner-icon="mdi-cash"
            class="filtro__adeudo"
          />
          <v-btn
            small
            depressed
            color="primary"
            :disabled="!idCicloInicio || !idCicloFin"
            :loading="consultando"
            @click="consultar"
          >
            <v-icon left small>mdi-magnify</v-icon>
            Consultar
          </v-btn>
          <v-btn small outlined color="green darken-1" :disabled="!rango" @click="exportar">
            <v-icon left small>mdi-microsoft-excel</v-icon>
            Excel
          </v-btn>
        </div>
      </v-card-text>
    </v-card>

    <!-- Cargando -->
    <template v-if="consultando">
      <v-skeleton-loader type="image" class="shadowCard mb-4" />
      <v-skeleton-loader type="table-tbody" class="shadowCard" />
    </template>

    <!-- Estado vacío -->
    <v-card v-else-if="!rango" class="shadowCard" elevation="0">
      <v-card-text class="text-center py-12">
        <v-icon size="64" color="grey lighten-1">mdi-chart-bar-stacked</v-icon>
        <div class="text-subtitle-1 mt-3">Selecciona año, ciclo inicial y ciclo final</div>
        <div class="text-body-2 grey--text">
          Pulsa «Consultar» para ver los inscritos por plantel en cada ciclo. El plantel y el adeudo se aplican al instante.
        </div>
      </v-card-text>
    </v-card>

    <!-- Una sección por escuela -->
    <template v-else>
      <v-card v-for="sec in secciones" :key="'sec-' + sec.escuela" class="shadowCard mb-4" elevation="0">
        <v-card-title class="py-3 seccion__titulo">
          <v-chip small label dark class="mr-2 font-weight-bold" :color="sec.escuela === 2 ? 'deep-orange' : 'primary'">
            {{ sec.nombre }}
          </v-chip>
          <span class="text-subtitle-1 font-weight-bold">
            Inscritos (NI + RI)
            <span v-if="sec.plantelNombre" class="grey--text text--darken-1">· {{ sec.plantelNombre }}</span>
            <span v-if="adeudo !== 'todos'" class="grey--text text--darken-1">· {{ etiquetaAdeudo }}</span>
          </span>
          <v-spacer />
          <span class="text-caption grey--text">
            Total del rango <strong class="grey--text text--darken-3">{{ formatoNumero(sec.totales.total) }}</strong>
            <v-icon x-small class="ml-3">mdi-cursor-default-click</v-icon>
            Clic en un número o barra para ver los alumnos
          </span>
        </v-card-title>
        <v-divider />

        <v-card-text>
          <apexchart type="bar" height="800" :options="sec.opciones" :series="sec.series" />
        </v-card-text>

        <div class="tabla-wrap">
          <table class="tabla">
            <thead>
              <tr>
                <th class="tabla__sucursal">{{ sec.plantelNombre ? 'Tipo' : 'Plantel' }}</th>
                <th v-for="c in rango.ciclos" :key="'h-' + c.id_ciclo">
                  <div class="tabla__ciclo-pre">CICLO</div>
                  {{ etiquetaCiclo(c) }}
                </th>
                <th class="tabla__col-total">Total</th>
              </tr>
            </thead>
            <tbody>
              <tr v-for="fila in sec.filas" :key="fila.clave">
                <td class="tabla__sucursal">{{ fila.etiqueta }}</td>
                <td v-for="(valor, i) in fila.valores" :key="fila.clave + '-' + i">
                  <a v-if="valor > 0" class="numero" :style="estiloChip(fila.color)" @click="verDetalle(sec, fila, i)">{{ formatoNumero(valor) }}</a>
                  <span v-else class="numero--cero">0</span>
                </td>
                <td class="tabla__col-total">
                  <span class="numero numero--fijo" :style="estiloChip(fila.color)">{{ formatoNumero(fila.total) }}</span>
                </td>
              </tr>
              <tr v-if="!sec.filas.length">
                <td :colspan="rango.ciclos.length + 2" class="text-center grey--text py-6">Sin inscritos en el rango</td>
              </tr>
            </tbody>
            <tfoot>
              <tr>
                <td class="tabla__sucursal">TOTAL</td>
                <td v-for="(valor, i) in sec.totales.valores" :key="'t-' + i">
                  <a v-if="valor > 0" class="numero" :style="estiloChip(COLOR_TOTAL)" @click="verDetalle(sec, null, i)">{{ formatoNumero(valor) }}</a>
                  <span v-else>0</span>
                </td>
                <td class="tabla__col-total">
                  <span class="numero numero--fijo" :style="estiloChip(COLOR_TOTAL)">{{ formatoNumero(sec.totales.total) }}</span>
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </v-card>
    </template>

    <!-- Detalle de alumnos -->
    <v-dialog v-model="dialogDetalle" max-width="1150px" scrollable>
      <v-card>
        <v-toolbar color="primary" dark flat dense>
          <v-icon left>mdi-account-details</v-icon>
          <v-toolbar-title class="text-subtitle-1">
            {{ detalleInfo.titulo }}
            <span class="text-caption ml-2 detalle__sub">
              {{ detalleInfo.escuela }} · {{ detalleInfo.sucursal }} · {{ detalleInfo.ciclo }}
              <template v-if="detalleInfo.adeudo"> · {{ detalleInfo.adeudo }}</template>
            </span>
          </v-toolbar-title>
          <v-spacer />
          <v-btn
            small
            depressed
            color="green darken-1"
            class="mr-2"
            :disabled="!detalle.length"
            @click="exportarDetalle"
          >
            <v-icon left small>mdi-microsoft-excel</v-icon>
            Excel
          </v-btn>
          <v-btn icon @click="dialogDetalle = false">
            <v-icon>mdi-close</v-icon>
          </v-btn>
        </v-toolbar>

        <v-card-text class="pt-4">
          <div class="d-flex flex-wrap align-center mb-3">
            <v-chip small class="mr-2 mb-1" color="primary" outlined>
              <v-icon small left>mdi-account-multiple</v-icon>
              {{ detalle.length }} alumnos
            </v-chip>
            <v-chip v-if="resumenDetalle.ni" small class="mr-2 mb-1" color="teal" outlined>NI: {{ resumenDetalle.ni }}</v-chip>
            <v-chip v-if="resumenDetalle.ri" small class="mr-2 mb-1" color="indigo" outlined>RI: {{ resumenDetalle.ri }}</v-chip>
            <v-spacer />
            <v-text-field
              v-model="buscarDetalle"
              placeholder="Buscar matrícula, alumno o grupo"
              prepend-inner-icon="mdi-magnify"
              outlined
              dense
              hide-details
              clearable
              class="detalle__buscar"
            />
          </div>

          <v-data-table
            :headers="headersDetalle"
            :items="detalle"
            :search="buscarDetalle"
            :loading="cargandoDetalle"
            loading-text="Cargando alumnos..."
            item-key="id_alumno"
            dense
            class="tabla-detalle"
            :items-per-page="25"
            :footer-props="{ 'items-per-page-options': [25, 50, 100, -1] }"
          >
            <template v-slot:item.matricula="{ item }">
              <span class="matricula">{{ item.matricula }}</span>
            </template>
            <template v-slot:item.alumno="{ item }">
              <div class="font-weight-medium">{{ item.alumno }}</div>
              <div class="text-caption grey--text">{{ item.plantel }}</div>
            </template>
            <template v-slot:item.grupo="{ item }">
              <span class="text-caption">{{ item.grupo }}</span>
            </template>
            <template v-slot:item.pagado="{ item }">{{ formatoMoneda(item.pagado) }}</template>
            <template v-slot:item.adeudo="{ item }">
              <span :class="Number(item.adeudo) > 0 ? 'red--text' : 'grey--text'">{{ formatoMoneda(item.adeudo) }}</span>
            </template>
            <template v-slot:item.beca="{ item }">
              <v-chip v-if="item.beca != null" x-small color="amber darken-2" dark>{{ item.beca }}%</v-chip>
              <span v-else class="grey--text">–</span>
            </template>
            <template v-slot:item.tipo="{ item }">
              <v-chip x-small label :color="item.tipo === 'NI' ? 'teal' : 'indigo'" dark>{{ item.tipo }}</v-chip>
            </template>
            <template v-slot:item.fecha_alta="{ item }">{{ formatoFecha(item.fecha_alta) }}</template>
          </v-data-table>
        </v-card-text>
      </v-card>
    </v-dialog>
  </v-container>
</template>

<script>
import Alerta from '@/components/alertas/Alerta.vue'
import validarErrores from '@/mixins/validarErrores'
import funcionesExcel from '@/mixins/funcionesExcel'

const ETIQUETAS_DETALLE = {
  ni: 'Nuevos ingresos (NI)',
  ri: 'Reinscritos (RI)',
  total: 'Inscritos (NI + RI)',
}

const TODOS = 'TODOS'

const PALETA = [
  '#1E88E5', '#00C49A', '#FFB300', '#F4516C', '#7E57C2', '#00ACC1', '#43A047', '#FB8C00',
  '#5C6BC0', '#26A69A', '#EC407A', '#8D6E63', '#29B6F6', '#9CCC65', '#FFCA28', '#AB47BC',
  '#EF5350', '#66BB6A', '#78909C', '#D4E157',
]
const COLORES_NI_RI = ['#009688', '#3F51B5']
const COLOR_TOTAL = '#455A64'

function colorTextoSobre (hex) {
  const n = parseInt(String(hex).replace('#', ''), 16)
  const luminancia = (0.299 * ((n >> 16) & 255) + 0.587 * ((n >> 8) & 255) + 0.114 * (n & 255)) / 255
  return luminancia > 0.65 ? 'rgba(0, 0, 0, 0.87)' : '#ffffff'
}

const OPCIONES_ADEUDO = [
  { text: 'Todos', value: 'todos' },
  { text: 'Sin adeudo', value: 'sin' },
  { text: 'Con adeudo', value: 'con' },
]

function sumarArreglos (a, b) {
  return a.map((v, i) => v + (b[i] || 0))
}

function restarArreglos (a, b) {
  return a.map((v, i) => v - (b[i] || 0))
}

/** Conteo de un tipo (ni / ri) según el filtro de adeudo. */
function conteoPorAdeudo (datos, tipo, adeudo) {
  const total = datos[tipo]
  const conAdeudo = datos[`${tipo}_adeudo`] || total.map(() => 0)
  if (adeudo === 'con') return conAdeudo
  if (adeudo === 'sin') return restarArreglos(total, conAdeudo)
  return total
}

function sumar (valores) {
  return valores.reduce((s, v) => s + v, 0)
}
export default {
  name: 'InscritosPorCiclo',
  components: { Alerta },
  mixins: [validarErrores, funcionesExcel],

  data () {
    return {
      ciclos: [],
      cargandoCiclos: false,
      anio: null,
      idCicloInicio: null,
      idCicloFin: null,
      idPlantel: TODOS,
      adeudo: 'todos',
      opcionesAdeudo: OPCIONES_ADEUDO,
      COLOR_TOTAL,
      plantelesCatalogo: [],
      consultando: false,
      rango: null,

      dialogDetalle: false,
      cargandoDetalle: false,
      detalleInfo: { titulo: '', sucursal: '', escuela: '', ciclo: '', adeudo: '' },
      detalle: [],
      buscarDetalle: '',
      headersDetalle: [
        { text: 'Matrícula', value: 'matricula' },
        { text: 'Alumno', value: 'alumno' },
        { text: 'Grupo', value: 'grupo' },
        { text: 'Pagado', value: 'pagado', align: 'end' },
        { text: 'Adeudo', value: 'adeudo', align: 'end' },
        { text: 'Beca', value: 'beca', align: 'center' },
        { text: 'Tipo', value: 'tipo', align: 'center' },
        { text: 'Fecha alta', value: 'fecha_alta' },
      ],
    }
  },

  computed: {
    ciclosAsc () {
      return [...this.ciclos].sort((a, b) =>
        String(a.fecha_inicio_ciclo).localeCompare(String(b.fecha_inicio_ciclo)) || a.id_ciclo - b.id_ciclo)
    },

    anios () {
      return [...new Set(this.ciclos.map((c) => c.anio).filter(Boolean))].sort((a, b) => b - a)
    },

    ciclosInicio () {
      return this.ciclosAsc.filter((c) => c.anio === this.anio)
    },

    cicloInicio () {
      return this.ciclos.find((c) => c.id_ciclo === this.idCicloInicio) || null
    },

    ciclosFin () {
      if (!this.cicloInicio) return []
      const desde = String(this.cicloInicio.fecha_inicio_ciclo)
      return this.ciclosAsc.filter((c) => String(c.fecha_inicio_ciclo) >= desde)
    },

    opcionesPlantel () {
      const mapa = new Map()
      for (const p of this.plantelesCatalogo) {
        mapa.set(Number(p.id_plantel), { id_plantel: Number(p.id_plantel), plantel: p.plantel, escuela: Number(p.escuela) })
      }
      for (const e of (this.rango && this.rango.escuelas) || []) {
        for (const p of e.planteles) {
          if (!mapa.has(p.id_plantel)) mapa.set(p.id_plantel, { id_plantel: p.id_plantel, plantel: p.plantel, escuela: e.escuela })
        }
      }
      const planteles = [...mapa.values()].sort((a, b) => String(a.plantel).localeCompare(String(b.plantel), 'es'))
      return [{ id_plantel: TODOS, plantel: 'TODOS' }, ...planteles]
    },

    etiquetaAdeudo () {
      const opcion = OPCIONES_ADEUDO.find((o) => o.value === this.adeudo)
      return opcion ? opcion.text : ''
    },

    plantelSeleccionado () {
      if (this.idPlantel === TODOS) return null
      return this.opcionesPlantel.find((p) => p.id_plantel === this.idPlantel) || null
    },

    secciones () {
      if (!this.rango) return []
      const plantel = this.plantelSeleccionado
      return this.rango.escuelas
        .filter((e) => !plantel || plantel.escuela === e.escuela || e.planteles.some((p) => p.id_plantel === plantel.id_plantel))
        .map((e) => this.armarSeccion(e, plantel))
    },

    resumenDetalle () {
      const r = { ni: 0, ri: 0 }
      for (const a of this.detalle) {
        r[a.tipo === 'NI' ? 'ni' : 'ri'] += 1
      }
      return r
    },
  },

  watch: {
    anio () {
      if (!this.ciclosInicio.some((c) => c.id_ciclo === this.idCicloInicio)) this.idCicloInicio = null
    },

    idCicloInicio () {
      if (!this.ciclosFin.some((c) => c.id_ciclo === this.idCicloFin)) this.idCicloFin = null
    },
  },

  created () {
    this.cargarCiclos()
    this.cargarPlanteles()
  },

  methods: {
    async cargarCiclos () {
      this.cargandoCiclos = true
      try {
        const response = await this.$http.get('kpi.inscritos.ciclo.ciclos', { params: { escuela: 1 } })
        this.ciclos = response.body || response.data || []
        const actual = new Date().getFullYear()
        this.anio = this.anios.includes(actual) ? actual : (this.anios[0] || null)
      } catch (error) {
        this.validarError(error)
      } finally {
        this.cargandoCiclos = false
      }
    },

    async cargarPlanteles () {
      try {
        const response = await this.$http.get('planteles.activos')
        this.plantelesCatalogo = response.body || response.data || []
      } catch (error) {
        this.plantelesCatalogo = []
      }
    },

    async consultar () {
      if (!this.idCicloInicio || !this.idCicloFin) return
      this.consultando = true
      this.rango = null
      try {
        const response = await this.$http.get('kpi.inscritos.ciclo.rango', {
          params: { id_ciclo_inicio: this.idCicloInicio, id_ciclo_fin: this.idCicloFin },
        })
        this.rango = response.body || response.data || null
      } catch (error) {
        this.validarError(error)
      } finally {
        this.consultando = false
      }
    },

    armarSeccion (escuela, plantel) {
      const ceros = this.rango.ciclos.map(() => 0)
      let filas

      if (!plantel) {
        filas = escuela.planteles
          .map((p) => ({
            clave: 'p-' + p.id_plantel,
            etiqueta: p.plantel,
            plantel: p.plantel,
            id_plantel: p.id_plantel,
            tipoDetalle: 'total',
            valores: sumarArreglos(conteoPorAdeudo(p, 'ni', this.adeudo), conteoPorAdeudo(p, 'ri', this.adeudo)),
          }))
          .filter((f) => f.valores.some((v) => v > 0))
      } else {
        const datos = escuela.planteles.find((p) => p.id_plantel === plantel.id_plantel) || { ni: ceros, ri: ceros }
        const base = { plantel: plantel.plantel, id_plantel: plantel.id_plantel }
        filas = [
          { ...base, clave: 'ni', etiqueta: 'Nuevo ingreso (NI)', tipoDetalle: 'ni', valores: conteoPorAdeudo(datos, 'ni', this.adeudo) },
          { ...base, clave: 'ri', etiqueta: 'Reinscritos (RI)', tipoDetalle: 'ri', valores: conteoPorAdeudo(datos, 'ri', this.adeudo) },
        ]
      }

      const colores = plantel ? COLORES_NI_RI : PALETA
      filas.forEach((f, i) => {
        f.total = sumar(f.valores)
        f.color = colores[i % colores.length]
      })
      const valores = ceros.map((_, i) => sumar(filas.map((f) => f.valores[i])))
      const seccion = {
        escuela: escuela.escuela,
        nombre: escuela.nombre,
        plantelNombre: plantel ? plantel.plantel : '',
        filas,
        totales: { valores, total: sumar(valores) },
        series: filas.map((f) => ({ name: f.etiqueta, data: f.valores })),
      }
      seccion.opciones = this.opcionesGrafica(seccion, colores)
      return seccion
    },

    estiloChip (color) {
      return { background: color, color: colorTextoSobre(color) }
    },

    opcionesGrafica (seccion, colores) {
      return {
        chart: {
          stacked: true,
          toolbar: { show: true },
          fontFamily: 'inherit',
          events: {
            dataPointSelection: (event, ctx, cfg) => {
              const fila = seccion.filas[cfg.seriesIndex]
              if (fila) this.verDetalle(seccion, fila, cfg.dataPointIndex)
            },
          },
        },
        colors: colores,
        plotOptions: {
          bar: {
            horizontal: false,
            columnWidth: '60%',
            borderRadius: 3,
            dataLabels: {
              total: { enabled: true, style: { fontSize: '12px', fontWeight: 700 } },
            },
          },
        },
        dataLabels: {
          enabled: true,
          style: { fontSize: '10px' },
          formatter: (val) => (val > 0 ? val : ''),
        },
        xaxis: { categories: this.rango.ciclos.map((c) => c.ciclo) },
        yaxis: { title: { text: 'Inscritos' } },
        grid: { borderColor: '#eeeeee' },
        legend: { position: 'bottom' },
        tooltip: { shared: false, intersect: true },
      }
    },

    verDetalle (seccion, fila, indice) {
      const ciclo = this.rango.ciclos[indice]
      const plantel = this.plantelSeleccionado
      const id_plantel = fila ? fila.id_plantel : (plantel ? plantel.id_plantel : null)
      this.cargarDetalle({
        escuela: seccion.escuela,
        id_ciclo: seccion.escuela === 2 ? ciclo.id_ciclo_fast : ciclo.id_ciclo,
        ciclo: seccion.escuela === 2 ? ciclo.ciclo_fast : ciclo.ciclo,
        id_plantel,
        plantel: id_plantel == null ? 'Todas las sucursales' : (fila ? fila.plantel : plantel.plantel),
        tipo: fila ? fila.tipoDetalle : 'total',
      })
    },

    async cargarDetalle (contexto) {
      this.detalleInfo = {
        titulo: ETIQUETAS_DETALLE[contexto.tipo],
        sucursal: contexto.plantel,
        escuela: contexto.escuela === 2 ? 'FAST' : 'INBI',
        ciclo: contexto.ciclo,
        adeudo: this.adeudo !== 'todos' ? this.etiquetaAdeudo : '',
      }
      this.detalle = []
      this.buscarDetalle = ''
      this.dialogDetalle = true
      this.cargandoDetalle = true
      try {
        const params = { escuela: contexto.escuela, id_ciclo: contexto.id_ciclo, tipo: contexto.tipo, adeudo: this.adeudo }
        if (contexto.id_plantel != null) params.id_plantel = contexto.id_plantel
        const response = await this.$http.get('kpi.inscritos.ciclo.detalle', { params })
        const data = response.body || response.data || {}
        this.detalle = data.alumnos || []
      } catch (error) {
        this.dialogDetalle = false
        this.validarError(error)
      } finally {
        this.cargandoDetalle = false
      }
    },

    etiquetaCiclo (ciclo) {
      return String(ciclo.ciclo).replace(/^CICLO\s*/i, '')
    },

    formatoNumero (valor) {
      return Number(valor || 0).toLocaleString('es-MX')
    },

    formatoMoneda (valor) {
      if (valor == null) return '—'
      return Number(valor).toLocaleString('es-MX', { style: 'currency', currency: 'MXN' })
    },

    formatoFecha (valor) {
      if (!valor) return '—'
      const [y, m, d] = String(valor).slice(0, 10).split('-')
      return d && m && y ? `${d}/${m}/${y}` : String(valor)
    },

    exportar () {
      if (!this.rango) return
      const filas = []
      for (const sec of this.secciones) {
        for (const f of [...sec.filas, { etiqueta: 'TOTAL', valores: sec.totales.valores, total: sec.totales.total }]) {
          const fila = { Escuela: sec.nombre, Plantel: sec.plantelNombre || f.etiqueta, Adeudo: this.etiquetaAdeudo }
          if (sec.plantelNombre) fila.Tipo = f.etiqueta
          this.rango.ciclos.forEach((c, i) => { fila[c.ciclo] = f.valores[i] })
          fila.Total = f.total
          filas.push(fila)
        }
      }
      const inicio = this.etiquetaCiclo(this.rango.ciclos[0])
      const fin = this.etiquetaCiclo(this.rango.ciclos[this.rango.ciclos.length - 1])
      const sufijoAdeudo = this.adeudo !== 'todos' ? `_${this.adeudo.toUpperCase()}_ADEUDO` : ''
      this.exportarExcelDirecto(filas, `INSCRITOS_${inicio}_A_${fin}${sufijoAdeudo}`)
    },

    exportarDetalle () {
      const filas = this.detalle.map((a) => ({
        Matrícula: a.matricula,
        Alumno: a.alumno,
        Sucursal: a.plantel,
        Grupo: a.grupo,
        Pagado: a.pagado,
        Adeudo: a.adeudo,
        'Beca %': a.beca,
        Tipo: a.tipo,
        'Fecha alta': this.formatoFecha(a.fecha_alta),
      }))
      this.exportarExcelDirecto(filas, `DETALLE_${this.detalleInfo.escuela}_${this.detalleInfo.ciclo}`.replace(/\s+/g, '_'))
    },
  },
}
</script>

<style scoped>
.encabezado__contenido {
  display: flex;
  align-items: center;
  justify-content: space-between;
  flex-wrap: wrap;
  gap: 12px;
  padding: 12px 16px !important;
}
.encabezado__titulo {
  flex: 1 1 0;
  min-width: 180px;
}
.encabezado__textos {
  min-width: 0;
}
.encabezado__subtitulo {
  white-space: nowrap;
  overflow: hidden;
  text-overflow: ellipsis;
}
.encabezado__filtros {
  display: flex;
  align-items: center;
  flex: 0 0 auto;
  flex-wrap: nowrap;
  gap: 8px;
  margin-left: auto;
}
.filtro__anio {
  width: 100px;
  flex: 0 0 100px;
}
.filtro__ciclo {
  width: 150px;
  flex: 0 0 150px;
}
.filtro__plantel {
  width: 190px;
  flex: 0 0 190px;
}
.filtro__adeudo {
  width: 185px;
  flex: 0 0 185px;
}
.seccion__titulo {
  flex-wrap: wrap;
  gap: 4px;
}

.tabla-wrap {
  overflow: auto;
  max-height: 520px;
  border-top: 1px solid #eeeeee;
}
.tabla {
  width: 100%;
  border-collapse: separate;
  border-spacing: 0;
  font-size: 13px;
}
.tabla th,
.tabla td {
  padding: 6px 8px;
  text-align: center;
  white-space: nowrap;
  border-bottom: 1px solid #f0f0f0;
}
.tabla thead th {
  position: sticky;
  top: 0;
  z-index: 2;
  background: #fafafa;
  color: #616161;
  font-weight: 600;
  font-size: 12px;
  border-bottom: 2px solid #e0e0e0;
}
.tabla__ciclo-pre {
  font-size: 10px;
  color: #9e9e9e;
  line-height: 1;
}
.tabla .tabla__sucursal {
  text-align: left;
  font-weight: 600;
  position: sticky;
  left: 0;
  z-index: 1;
  background: #ffffff;
  min-width: 170px;
}
.tabla thead .tabla__sucursal {
  z-index: 3;
  background: #fafafa;
}
.tabla tbody tr:hover td {
  background: #f5f9ff;
}
.tabla__col-total {
  background: #f7f9fc;
  min-width: 70px;
}
.tabla tfoot td {
  position: sticky;
  bottom: 0;
  z-index: 1;
  background: #eceff1;
  font-weight: 700;
  border-top: 2px solid #cfd8dc;
}
.tabla tfoot .tabla__sucursal {
  z-index: 2;
  background: #eceff1;
}

.numero {
  display: inline-block;
  min-width: 36px;
  padding: 2px 8px;
  border-radius: 12px;
  font-weight: 600;
  text-decoration: none;
  cursor: pointer;
  box-shadow: 0 1px 2px rgba(0, 0, 0, 0.15);
  transition: filter 0.15s ease, transform 0.15s ease;
}
.numero:hover {
  filter: brightness(0.9);
  transform: translateY(-1px);
}
.numero--fijo {
  font-weight: 700;
  cursor: default;
}
.numero--fijo:hover {
  filter: none;
  transform: none;
}
.numero--cero {
  color: #bdbdbd;
}

.detalle__sub {
  opacity: 0.85;
}
.detalle__buscar {
  max-width: 340px;
}
.matricula {
  font-family: monospace;
  font-size: 12px;
}
.tabla-detalle ::v-deep td {
  vertical-align: middle;
}
</style>
